import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';

// Types
interface DensidadLGS {
  id?: string;
  value: number;
  active: boolean;
}

interface ContainerMeasures {
  id?: string;
  altura_max: number;
  ancho: number;
  base: number;
  largo: number;
  arriba: number;
}

interface LGSCalculationsSectionProps {
  data: {
    densidadesLGS: DensidadLGS[];
    containerMeasures: ContainerMeasures | null;
  };
  operations: {
    handleDensidadLGSSubmit: (data: Record<string, any>) => Promise<void>;
    handleDensidadLGSToggle: (item: DensidadLGS) => Promise<void>;
    handleDensidadLGSDelete: (item: DensidadLGS) => Promise<void>;
    handleDensidadLGSEdit: (item: DensidadLGS, key: string, newValue: any) => Promise<void>;
    handleContainerMeasuresUpdate: (data: ContainerMeasures) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const LGSCalculationsSection: React.FC<LGSCalculationsSectionProps> = ({
  data,
  operations,
  userPermissions
}) => {
  // Densidades LGS Table Columns
  const densidadLGSColumns: TableColumn<DensidadLGS>[] = [
    {
      key: 'value',
      title: 'Densidad (g/cm³)',
      width: '80%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleDensidadLGSEdit,
      render: (value) => `${value} g/cm³`
    }
  ];

  // Form fields for Densidades LGS
  const densidadLGSFormFields = [
    {
      key: 'value',
      name: 'value',
      label: 'Densidad (g/cm³)',
      type: 'number' as const,
      required: true,
      step: 0.1,
      min: 2.0,
      max: 2.6
    }
  ];

  // Quick Setup Handler for Densidades LGS
  const handleDensidadesQuickSetup = async () => {
    if (window.confirm('¿Cargar densidades LGS estándar? Esta acción agregará 7 valores de densidad.')) {
      const standardDensities = [2.6, 2.5, 2.4, 2.3, 2.2, 2.1, 2.0];

      try {
        for (const value of standardDensities) {
          await operations.handleDensidadLGSSubmit({
            value,
            active: true
          });
        }
        alert('✅ Densidades LGS estándar cargadas exitosamente');
      } catch (error) {
        console.error('Error cargando densidades LGS:', error);
        alert('❌ Error cargando densidades LGS. Ver consola para detalles.');
      }
    }
  };

  // Container Measures Component
  const ContainerMeasuresEditor: React.FC = () => {
    const [measures, setMeasures] = React.useState<ContainerMeasures>(
      data.containerMeasures || {
        altura_max: 1.1,
        ancho: 1.67,
        base: 1.8,
        largo: 3.05,
        arriba: 1.67
      }
    );

    const [isEditing, setIsEditing] = React.useState(false);

    React.useEffect(() => {
      if (data.containerMeasures) {
        setMeasures(data.containerMeasures);
      }
    }, [data.containerMeasures]);

    const handleSave = async () => {
      try {
        await operations.handleContainerMeasuresUpdate(measures);
        setIsEditing(false);
        alert('✅ Dimensiones de contenedor guardadas exitosamente');
      } catch (error) {
        console.error('Error guardando dimensiones:', error);
        alert('❌ Error guardando dimensiones. Ver consola para detalles.');
      }
    };

    const handleLoadDefaults = () => {
      if (window.confirm('¿Cargar dimensiones por defecto? Esto sobrescribirá los valores actuales.')) {
        setMeasures({
          altura_max: 1.1,
          ancho: 1.67,
          base: 1.8,
          largo: 3.05,
          arriba: 1.67
        });
        setIsEditing(true);
      }
    };

    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '25px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginTop: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#0288d1', margin: 0 }}>📐 Dimensiones de Contenedores</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleLoadDefaults}
              disabled={!userPermissions.canEditReports}
              style={{
                padding: '8px 16px',
                backgroundColor: userPermissions.canEditReports ? '#ff9800' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: userPermissions.canEditReports ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              📦 Cargar Valores Por Defecto
            </button>
            {isEditing && (
              <button
                onClick={handleSave}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                💾 Guardar
              </button>
            )}
          </div>
        </div>

        <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
          Configuración global de las dimensiones físicas de los contenedores utilizados para cálculo de volumen.
          Estas dimensiones se aplican a todos los contenedores en el sistema.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Altura Máxima (m)
            </label>
            <input
              type="number"
              step="0.01"
              value={measures.altura_max}
              onChange={(e) => {
                setMeasures({ ...measures, altura_max: parseFloat(e.target.value) });
                setIsEditing(true);
              }}
              disabled={!userPermissions.canEditReports}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Ancho (m)
            </label>
            <input
              type="number"
              step="0.01"
              value={measures.ancho}
              onChange={(e) => {
                setMeasures({ ...measures, ancho: parseFloat(e.target.value) });
                setIsEditing(true);
              }}
              disabled={!userPermissions.canEditReports}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Base (m)
            </label>
            <input
              type="number"
              step="0.01"
              value={measures.base}
              onChange={(e) => {
                setMeasures({ ...measures, base: parseFloat(e.target.value) });
                setIsEditing(true);
              }}
              disabled={!userPermissions.canEditReports}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Largo (m)
            </label>
            <input
              type="number"
              step="0.01"
              value={measures.largo}
              onChange={(e) => {
                setMeasures({ ...measures, largo: parseFloat(e.target.value) });
                setIsEditing(true);
              }}
              disabled={!userPermissions.canEditReports}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Arriba (m)
            </label>
            <input
              type="number"
              step="0.01"
              value={measures.arriba}
              onChange={(e) => {
                setMeasures({ ...measures, arriba: parseFloat(e.target.value) });
                setIsEditing(true);
              }}
              disabled={!userPermissions.canEditReports}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          border: '1px solid #0288d1'
        }}>
          <strong style={{ color: '#01579b' }}>💡 Fórmula de Volumen:</strong>
          <div style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '13px', color: '#424242' }}>
            V = altura × arriba × (((altura / altura_max) × (largo - base) / 2) + base)
          </div>
          <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
            Esta fórmula calcula el volumen de contenedores trapezoidales donde la anchura varía linealmente
            desde la base hasta el tope a medida que aumenta la altura.
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🧮 Cálculos y Mediciones LGS</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Configura las densidades LGS para cálculos de rendimiento y las dimensiones de contenedores para volumen
        </p>
      </div>

      {/* Densidades LGS Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Densidades LGS',
          '⚖️',
          data.densidadesLGS.filter(d => d.id) as Array<DensidadLGS & { id: string }>,
          densidadLGSFormFields,
          densidadLGSColumns,
          operations.handleDensidadLGSSubmit,
          operations.handleDensidadLGSToggle,
          operations.handleDensidadLGSDelete,
          {
            description: 'Define las densidades LGS disponibles para selección en reportes. Usadas en cálculos de Rendimiento (l/kg).',
            quickSetup: {
              title: 'Carga Rápida de Densidades LGS',
              description: 'Carga los 7 valores estándar: 2.6, 2.5, 2.4, 2.3, 2.2, 2.1, 2.0 g/cm³',
              buttonLabel: '⚖️ Cargar Densidades Estándar',
              onQuickSetup: handleDensidadesQuickSetup
            }
          }
        )}
      />

      {/* Container Measures Section */}
      <ContainerMeasuresEditor />
    </>
  );
};
